import React, { FC, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import times from 'lodash/times'
import BellOffIcon from 'transfix-icons/build/BellOffIcon'
import BellOnIcon from 'transfix-icons/build/BellOnIcon'
import InfoIcon from 'transfix-icons/build/InfoIcon'
import { Table, TableBody, TableRow } from 'transfix-ui/components/Table'
import { Box, makeStyles, Typography, Skeleton } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useBookableShipmentAuctionSearchContext } from 'apps/fleet-planner/context/BookableShipmentAuctionSearchContext'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useTruckAvailabilityToggle } from 'apps/fleet-planner/hooks/useTruckAvailabilityToggle'
import { MarketplaceReloadsCardRow } from 'apps/fleet-planner/pages/marketplace/MarketplaceReloadsCardRow'
import { MarketplaceReloadsRow } from 'apps/fleet-planner/pages/marketplace/MarketplaceReloadsRow'
import { IStop, AuctionShipment } from 'apps/fleet-planner/types'
import { getReloadsPickupStartDate, getReloadsStopData } from 'apps/fleet-planner/utils/reloads'
import { FormModal, Switch, Toast, Tooltip } from 'transfix-ui'
import FORMATS from 'constants/datetime'

const usePostTruckReminderStyles = makeStyles(() => ({
  text: {
    color: brandColors.coolGray6,
    paddingTop: 0,
  },
}))

interface IBookableShipmentSavedSearchPostTruckReminderModalProps {
  stops: IStop[]
}

export const BookableShipmentSavedSearchPostTruckReminderModal: FC<IBookableShipmentSavedSearchPostTruckReminderModalProps> = ({
  stops = [],
}) => {
  const { trackEvent } = useFMSAnalytics()
  const {
    auctionShipments: shipments,
    isLoading: isReloadsLoading,
  } = useBookableShipmentAuctionSearchContext()
  const history = useHistory()
  const { pathname = '' } = useLocation()
  const { isMobile } = useBreakpointContext()
  const [isNotificationOn, setIsNotificationOn] = useState<boolean>(false)
  const {
    closeToast,
    createTruckAvailability,
    createdSavedSearchId,
    deleteTruckAvailability,
    isLoading: isTruckAvailabilityLoading,
    toastMessage,
    toastOpen,
    toastType,
  } = useTruckAvailabilityToggle()
  const { city, displayTimezone: timezone, endAt, startAt = '', stateCode } = getReloadsStopData(
    stops
  )

  const onCloseHandler = () => {
    if (shipments.length === 0) trackEvent('Truck Availability', 'NOT_PRESENTED_WITH_RELOADS')
    history.push(pathname.replace(/(\/post-truck)/, ''))
  }

  const notificationToggleHandler = () => {
    setIsNotificationOn(prev => !prev)
    if (createdSavedSearchId) {
      deleteTruckAvailability()
    } else {
      trackEvent('Truck Availability', 'POSTED_TRUCK_AVAILABILITY_FROM_RELOADS_MODAL')
      const pickupStartDate = getReloadsPickupStartDate(startAt, endAt, timezone).format(
        FORMATS.fleetPlannerApiFormat
      )
      createTruckAvailability({
        bookableShipmentSavedSearch: {
          pickupCity: city,
          pickupMiles: 50,
          pickupStartDate,
          pickupStateCode: stateCode,
          sourceType: 'dispatcher_truck_availability',
        },
      })
    }
  }

  const classes = usePostTruckReminderStyles()
  return (
    <FormModal
      title='Your load was booked! Get notified about reloads?'
      onClose={onCloseHandler}
      size={8}
      open
      actions={[
        {
          label: 'Close',
          type: 'dismiss',
        },
      ]}>
      <Box pb={2}>
        {isReloadsLoading ? (
          <Skeleton variant='rectangular' height='20px' width='100%' />
        ) : (
          <Typography className={classes.text} variant='body2'>
            {shipments.length
              ? 'Get notified about future reloads matching this load.'
              : `Transfix can notify you about loads that are picking up where you’re dropping off so you can always keep your trucks on the road.`}
          </Typography>
        )}
      </Box>
      <Box>
        {/* Reloads section loading state */}
        {isReloadsLoading && (
          <Table>
            <TableBody>
              {times(3).map(item => (
                <TableRow key={item}>
                  <Box pb={2}>
                    <Skeleton variant='rectangular' height='60px' width='100%' />
                  </Box>
                </TableRow>
              ))}
            </TableBody>
            <></>
          </Table>
        )}
        {/* Reloads section */}
        {!isReloadsLoading && shipments.length > 0 && (
          <Table>
            <TableBody>
              {shipments
                .slice(0, 3)
                .map((shipment: AuctionShipment) =>
                  isMobile ? (
                    <MarketplaceReloadsCardRow shipment={shipment} />
                  ) : (
                    <MarketplaceReloadsRow key={shipment?.id} shipment={shipment} />
                  )
                )}
            </TableBody>
            <></>
          </Table>
        )}
        {/* Post truck availability section */}
        <Typography variant='body2'>Not seeing loads that work?</Typography>
        <Box display='flex' alignItems='center'>
          {isNotificationOn ? <BellOnIcon size='large' /> : <BellOffIcon size='large' />}
          <Box pl={1} pr={0.5}>
            <Typography variant='body2'>Get notified about reloads for this load</Typography>
          </Box>
          <Tooltip position='top' trigger={<InfoIcon />}>
            We&quot;ll send emails about loads that pickup where this load drops off.
          </Tooltip>
          <Box pl={3}>
            <Switch
              checked={isNotificationOn}
              onChange={notificationToggleHandler}
              disabled={isTruckAvailabilityLoading}
            />
          </Box>
        </Box>
      </Box>
      <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
    </FormModal>
  )
}
